import type { ReactNode } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { DetailMobileAppChrome } from '~/ui-rocco/pages/detail-shared';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { ProfessionalHeroSection } from './sections/ProfessionalHeroSection';
import { ProfessionalIdentityStack } from './sections/ProfessionalIdentityStack';
import { ProfessionalOpeningRow } from './sections/ProfessionalOpeningRow';
import { ProfessionalStickyCtas } from './sections/ProfessionalStickyCtas';
import { ProfessionalProfileSummarySection } from './sections/ProfessionalProfileSummarySection';
import { ProfileRailDockProvider } from './hooks/use-profile-rail-dock';
import { ProfessionalProfileHero } from './sections/ProfessionalProfileHero';
import { ProfessionalProfileHeader } from './sections/ProfessionalProfileHeader';
import { ProfessionalSubNav } from './sections/ProfessionalSubNav';
import { ProfessionalDesktopSubNav } from './sections/ProfessionalDesktopSubNav';
import { ProfessionalDetailsMagicIsland } from './magic-island/ProfessionalDetailsMagicIsland';
import { ProfessionalIslandFallback } from './magic-island/ProfessionalIslandFallback';

interface ProfessionalRevampShellProps {
    /**
     * Business Manager admin controls (edit toggle, copy link). Public site
     * only — inside the preview iframe they would be a second set of chrome
     * over the top of the one already around it.
     */
    adminControls?: ReactNode;
    children: ReactNode;
}

/**
 * The persistent chrome for the revamped professional page: the header —
 * banner, identity/stats/CTA and the tab bar — plus the Magic Island.
 *
 * All of it lives above the tab body so it survives tab navigation and never
 * re-flashes; only the body below the nav swaps. Desktop opens as the product
 * page does — badge on the banner's edge, tabs on the trail's line, the
 * statement with a sticky CTA pair beside it (BC-5230); the social-profile
 * layout is mobile-only.
 *
 * Shared by the public professional layout and the Business Manager preview,
 * which render the same page under different parent routes.
 */
export const ProfessionalRevampShell = ({
    adminControls,
    children,
}: ProfessionalRevampShellProps) => {
    const { desktop } = useAppDisplayModeContext();

    return (
        <ProfessionalDetailsMagicIsland>
            <ProfessionalIslandFallback />
            {adminControls}
            <DetailMobileAppChrome />
            {desktop ? (
                <div className="relative">
                    <ProfessionalHeroSection />
                    {/* Profile details, parked — the component holds its own
                        flag. */}
                    <ProfessionalIdentityStack />
                </div>
            ) : (
                <>
                    <ProfessionalProfileHero />
                    <ProfessionalProfileHeader />
                </>
            )}
            {/* Everything under the banner shares one box so the CTA pair can
                stay locked to the viewport across it: a sticky element travels
                only as far as its own parent, and this parent ends before the
                footer. It carries the page's ground, so the run between the
                band and the summary is grey rather than the root's white — and
                it is a flow root, so the badge's pull onto the banner stays
                inside it rather than collapsing through and dragging the
                ground up over the banner's foot. */}
            <div className="relative flow-root bg-ground ground-grey">
                {/* Desktop only: below md the floating chat chrome owns this
                    corner, and two controls there would fight for it. */}
                {desktop ? <BackToTop white /> : null}
                {desktop ? (
                    <ProfileRailDockProvider>
                        {/* The product page's opening: badge on the banner's
                            edge, the tabs on the trail's line with the
                            profile's figures, then the statement with the ways
                            in beside it — a sticky pair that docks into the
                            band. */}
                        <ProfessionalOpeningRow />
                        <ProfessionalDesktopSubNav />
                        <ProfessionalStickyCtas />
                        <ProfessionalProfileSummarySection />
                    </ProfileRailDockProvider>
                ) : (
                    <ProfessionalSubNav />
                )}
                {children}
            </div>
        </ProfessionalDetailsMagicIsland>
    );
};
